import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";

import CustomAppBar from "../customAppBar";
import SideNavigation from "../sideNavigation";
import { useTenant } from "../../hooks/useTenant";
import { useAuth } from "../../context/AuthContext";
import { useDashContext } from "../../context/DashboardContext";
import { useApi } from "../../hooks/useApi";
import BarberOverview from "./barberOverview";
import BarberAppointments from "./barberAppointments";
import BarberSchedule from "./barberSchedule";
import BarberServices from "./barberServices";
import BarberAnalytics from "./barberAnalytics";
import BarberClients from "./barberClients";
import BarberExceptions from "./barberExceptions";
import BarberProfile from "./barberProfile";

const SLUG_TO_TAB = {
  overview: "Overview",
  appointments: "Appointments",
  schedule: "Schedule",
  services: "Services",
  exceptions: "Exceptions",
  clients: "Clients",
  analytics: "Analytics",
  payments: "Payments",
  profile: "Profile",
};

const BarberDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { tenant } = useTenant();
  const { user } = useAuth();
  const { barberTab, setBarberTab } = useDashContext();
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const slug = location.pathname.split("/")[2];
    if (!slug || !SLUG_TO_TAB[slug]) {
      navigate("/dashboard/overview", { replace: true });
      return;
    }
    setBarberTab(SLUG_TO_TAB[slug]);
  }, [location.pathname]);

  const renderContent = () => {
    switch (barberTab) {
      case "Appointments":
        return <BarberAppointments />;
      case "Schedule":
        return <BarberSchedule />;
      case "Services":
        return <BarberServices />;
      case "Exceptions":
        return <BarberExceptions />;
      case "Clients":
        return <BarberClients />;
      case "Analytics":
        return <BarberAnalytics />;
      case "Payments":
        return <PaymentsTab api={api} />;
      case "Profile":
        return <BarberProfile />;
      default:
        return <BarberOverview />;
    }
  };

  return (
    <>
      <CustomAppBar tenant={tenant} showLoginButton={false} />

      <Box
        sx={{
          mt: "64px",
          display: "flex",
          flexDirection: "row",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            width: "220px",
            flexShrink: 0,
            position: "sticky",
            top: "64px",
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          <SideNavigation />
        </Box>

        <Box
          sx={{ flex: 1, minWidth: 0, backgroundColor: "background.default" }}
        >
          {renderContent()}
        </Box>
      </Box>
    </>
  );
};

// ─── Payments Tab ─────────────────────────────────────────────────────────────
const PaymentsTab = ({ api }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripe = params.get("stripe");
    if (stripe === "success") {
      setStripeStatus("connected");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (stripe === "incomplete") {
      setError("You didn't finish connecting Stripe. Please try again.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (stripe === "error") {
      setError("Something went wrong with Stripe. Please try again.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const checkStripeStatus = async () => {
      try {
        const res = await api("/stripe/connect.php", { method: "GET" });
        setStripeStatus(
          res.data.already_connected ? "connected" : "not_connected",
        );
      } catch {
        setStripeStatus("not_connected");
      } finally {
        setStatusLoading(false);
      }
    };
    checkStripeStatus();
  }, []);

  const handleConnectStripe = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api("/stripe/connect.php", { method: "GET" });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      setError(err.message || "Failed to connect Stripe. Please try again.");
      setLoading(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api("/stripe/connect.php", { method: "GET" });
      if (res.data.url) window.open(res.data.url, "_blank");
    } catch (err) {
      setError(err.message || "Failed to open Stripe dashboard.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, pt: 5, pb: 10 }}
    >
      <Typography variant={isMobile ? "h4" : "h3"} sx={{ mb: 0.5 }}>
        Payments
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Connect your Stripe account to receive payments directly when clients
        book with you.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stripe card */}
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <AccountBalanceWalletOutlinedIcon
            sx={{ color: "text.third", fontSize: "1.4rem" }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Stripe Account
          </Typography>

          {!statusLoading && stripeStatus === "connected" && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: "0.9rem" }} />}
              label="Connected"
              size="small"
              color="success"
              sx={{ ml: "auto" }}
            />
          )}
          {!statusLoading && stripeStatus === "not_connected" && (
            <Chip
              icon={<ErrorOutlineOutlinedIcon sx={{ fontSize: "0.9rem" }} />}
              label="Not Connected"
              size="small"
              color="error"
              sx={{ ml: "auto" }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {statusLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={16} sx={{ color: "text.secondary" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Checking Stripe status...
            </Typography>
          </Box>
        ) : stripeStatus === "connected" ? (
          <>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2.5, lineHeight: 1.6 }}
            >
              Your Stripe account is connected. Clients will be charged
              automatically when they book with you.
            </Typography>
            <Button
              onClick={handleOpenStripeDashboard}
              disabled={loading}
              variant="outlined"
              endIcon={
                loading ? (
                  <CircularProgress size={14} />
                ) : (
                  <OpenInNewIcon sx={{ fontSize: "0.9rem" }} />
                )
              }
            >
              Open Stripe Dashboard
            </Button>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2.5, lineHeight: 1.6 }}
            >
              You haven't connected a Stripe account yet. Once connected, you'll
              receive payments directly when clients book with you.
            </Typography>
            <Button
              variant="contained"
              onClick={handleConnectStripe}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress
                    size={14}
                    sx={{ color: "primary.contrastText" }}
                  />
                ) : null
              }
            >
              {loading ? "Redirecting to Stripe..." : "Connect Stripe Account"}
            </Button>
          </>
        )}
      </Box>

      {/* How it works */}
      <Box
        sx={{
          mt: 2,
          p: 2.5,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <Typography variant="overline" sx={{ display: "block", mb: 1 }}>
          How it works
        </Typography>
        {[
          "Connect your Stripe account once — takes about 2 minutes.",
          "When a client books with you, they pay at the time of booking.",
          "The payment goes directly to your Stripe account.",
          "You can view your payouts anytime from your Stripe dashboard.",
        ].map((point, i) => (
          <Box key={i} sx={{ display: "flex", gap: 1.5, mb: i < 3 ? 1 : 0 }}>
            <Typography variant="body2" sx={{ color: "text.third" }}>
              {i + 1}.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", lineHeight: 1.6 }}
            >
              {point}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BarberDashboard;
